import RoundButton from '@basic/RoundButton';
import { DEFAULT_LANDING_PAGE_ACCENT_COLOR, UIPaths } from '@consts';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { usePaymentAndOrder } from '../../payments/PaymentAndOrderContext';
import { LANDING_CTA_GLOW_CLASS, accentGlowStyle } from './accentGlow';
import { useLandingPageTranslation } from './useLandingPageTranslation';

interface GoalLandingPageJumpToMainCTAButtonProps extends React.HTMLAttributes<HTMLElement> {
  type: 'menu' | 'hero';
}

export default function GoalLandingPageJumpToMainCTAButton({
  type = 'menu'
}: GoalLandingPageJumpToMainCTAButtonProps) {
  const router = useRouter();
  const { state, isSubscribed, isSoldOut, isFree, canEnroll } = usePaymentAndOrder();
  const { t } = useLandingPageTranslation();
  const accentColor = state.goal?.landingPage?.accentColor || DEFAULT_LANDING_PAGE_ACCENT_COLOR;

  const signupButtonText = useMemo(() => {
    // highest priority - if subscribed, then we should redirect to the course page
    if (isSubscribed) {
      return t('domains.payments.CTAs.learnNow');
    }

    // second priority - if sold out, then we should redirect to the recommended goals section
    if (isSoldOut) {
      return t('domains.payments.CTAs.soldOut');
    }

    const ctaLabel = state.goal?.landingPage?.hero?.ctaLabel;

    // third priority - if free or paid, then we should redirect to the signup page
    if (isFree) {
      return ctaLabel ?? t('domains.payments.CTAs.free');
    } else if (canEnroll) {
      return ctaLabel ?? t('domains.payments.CTAs.enrollNow');
    } else {
      return ctaLabel ?? t('domains.payments.CTAs.buyNow');
    }
  }, [isSubscribed, isSoldOut, isFree, canEnroll, state.goal?.landingPage?.hero?.ctaLabel]);

  const onClick = useCallback(() => {
    if (isSubscribed) {
      router.push(UIPaths.assignments(state.goal?.entityId, state.goal?.subscription?.id));
      return;
    }

    if (isSoldOut) {
      const element = document.getElementById('recommendations');
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const element = document.getElementById('pricing-card');
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isSubscribed, isSoldOut]);

  const minWidth = signupButtonText.length > 10 ? 'min-w-[200px]' : 'min-w-[127px]';
  const minWidthClass = type === 'menu' ? `@[1024px]:${minWidth}` : `@[640px]:${minWidth}`;
  const showGlow = type === 'hero';
  const balanceClass = type === 'menu' ? ' text-balance' : '';

  return (
    <RoundButton
      onClick={onClick}
      type={type === 'menu' ? 'primary' : 'color'}
      size={type === 'menu' ? 'medium' : 'large'}
      color={accentColor}
      style={showGlow ? accentGlowStyle(accentColor) : undefined}
      className={`w-full @[640px]:w-auto ${minWidthClass}${balanceClass}${showGlow ? ` ${LANDING_CTA_GLOW_CLASS}` : ''}`}
    >
      {signupButtonText}
    </RoundButton>
  );
}
